import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, FolderOpen, BarChart3, Settings, LogOut, Loader2, Images, Calculator, Phone } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useSeo } from "../../lib/seo";
import { ROLES, COMMERCIAL, TONE } from "../../lib/labels";
import { FeedProvider, useFeed } from "./useFeed";

const Ctx = createContext(null);
export const useStaff = () => useContext(Ctx);

export function StaffProvider({ children }) {
  const [user, setUser] = useState(null);
  useEffect(() => { api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false)); }, []);
  const logout = async () => { await api.post("/auth/logout"); setUser(false); };
  return <Ctx.Provider value={{ user, setUser, logout }}>{children}</Ctx.Provider>;
}

export function AdminLogin() {
  useSeo({ title: "Administration", path: "/admin/login", noindex: true });
  const { user, setUser } = useStaff();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/admin" replace />;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const r = await api.post("/auth/login", f); setUser(r.data); nav("/admin"); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex k-weave-dark text-[#FBF9F5] p-12 flex-col justify-between">
        <span className="font-display text-3xl tracking-[0.18em]">KOUOH</span>
        <p className="font-display text-4xl italic max-w-md">Tout est dans le dossier. On sait ce qu'il manque et ce qu'il faut faire ensuite.</p>
      </div>
      <form onSubmit={submit} className="flex flex-col justify-center gap-4 p-8 sm:p-16 max-w-lg w-full mx-auto">
        <p className="k-eyebrow">Administration</p>
        <h1 className="k-h2">Connexion de l'équipe</h1>
        <label className="k-label mt-4" htmlFor="ad-email">Email</label>
        <input id="ad-email" type="email" className="k-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-login-email" required />
        <label className="k-label" htmlFor="ad-pwd">Mot de passe</label>
        <input id="ad-pwd" type="password" className="k-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-login-password" required />
        {err && <p className="text-sm text-[#A84C2A]" role="alert" data-testid="admin-login-error">{err}</p>}
        <button className="k-btn mt-2" disabled={busy} data-testid="admin-login-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : "Se connecter"}</button>
        <Link to="/" className="text-sm k-link mt-4 self-start">Retour au site</Link>
      </form>
    </div>
  );
}

const NAV = [["/admin", "Tableau de bord", LayoutDashboard, "admin-nav-dashboard", true], ["/admin/dossiers", "Dossiers", FolderOpen, "admin-nav-dossiers"], ["/admin/realisations", "Réalisations", Images, "admin-nav-cases"], ["/admin/produits", "Produits", Images, "admin-nav-products"], ["/admin/contacts", "Contacts", Phone, "admin-nav-leads"], ["/admin/calculateurs", "Calculateurs", Calculator, "admin-nav-calc"], ["/admin/statistiques", "Statistiques", BarChart3, "admin-nav-stats"], ["/admin/parametres", "Paramètres", Settings, "admin-nav-settings"]];

export function AdminLayout() {
  useSeo({ title: "Administration", path: "/admin", noindex: true });
  const { user, logout } = useStaff();
  if (user === null) return <p className="p-10 text-sm">Chargement…</p>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return <FeedProvider enabled={!!user}><AdminFrame user={user} logout={logout} /></FeedProvider>;
}

function NavBadge() {
  const { unread } = useFeed();
  if (!unread) return null;
  return <span className="ml-auto min-w-[1.4rem] h-5 px-1.5 rounded-full bg-[#C25E38] text-white text-[11px] font-semibold inline-flex items-center justify-center" data-testid="nav-unread-badge">{unread}</span>;
}

function AdminFrame({ user, logout }) {
  const [scale, setScale] = useState(() => Number(localStorage.getItem("kouoh_admin_scale")) || 1);
  useEffect(() => { localStorage.setItem("kouoh_admin_scale", String(scale)); }, [scale]);
  const step = (d) => setScale((s) => Math.min(1.3, Math.max(0.9, Math.round((s + d) * 100) / 100)));
  return (
    <div className="k-admin min-h-screen lg:grid lg:grid-cols-[260px_1fr] bg-[#F6F2EC]" style={{ "--k-scale": scale }}>
      <aside className="bg-[#FFFDFA] border-b lg:border-b-0 lg:border-r border-[#E8E2D8] lg:min-h-screen p-4 lg:p-6 flex lg:flex-col gap-4 lg:gap-10 items-center lg:items-stretch no-print overflow-x-auto">
        <Link to="/admin" className="font-display text-2xl tracking-[0.18em] shrink-0 text-[#1C1917]">KOUOH <span className="hidden lg:inline font-mono-k text-[0.6rem] tracking-[0.2em] text-[#8C857B] align-middle ml-1">ATELIER</span></Link>
        <nav className="flex lg:flex-col gap-1 flex-1">
          {NAV.filter(([to]) => !(user.role === "production" && ["/admin/statistiques", "/admin/realisations"].includes(to))).map(([to, l, Icon, id, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={id} className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 text-sm whitespace-nowrap rounded-xl transition-colors duration-200 ${isActive ? "bg-[#FDF1EA] text-[#A84C2A] font-medium" : "text-[#57534E] hover:bg-[#F4F0EA] hover:text-[#1C1917]"}`}>
              <Icon size={17} strokeWidth={1.8} /> <span className="hidden sm:inline">{l}</span>{end && <NavBadge />}
            </NavLink>
          ))}
        </nav>
        <div className="text-xs text-[#8C857B] shrink-0 lg:border-t lg:border-[#E8E2D8] lg:pt-5">
          <div className="flex items-center gap-1 mb-3" aria-label="Taille du texte" data-testid="admin-font-scale">
            <span className="hidden lg:inline mr-1">Texte</span>
            <button onClick={() => step(-0.1)} className="w-8 h-8 rounded-lg border border-[#E3DCD1] hover:bg-[#F4F0EA] text-[#1C1917] text-xs" aria-label="Réduire le texte" data-testid="admin-font-smaller">A−</button>
            <button onClick={() => step(0.1)} className="w-8 h-8 rounded-lg border border-[#E3DCD1] hover:bg-[#F4F0EA] text-[#1C1917] text-base font-medium" aria-label="Agrandir le texte" data-testid="admin-font-larger">A+</button>
            <span className="font-mono-k ml-1" data-testid="admin-font-value">{Math.round(scale * 100)} %</span>
          </div>
          <p className="text-[#1C1917] font-medium hidden lg:block" data-testid="admin-user-name">{user.name}</p>
          <p className="hidden lg:block">{ROLES[user.role]}</p>
          <button onClick={logout} className="mt-2 flex items-center gap-2 hover:text-[#A84C2A] transition-colors" data-testid="admin-logout"><LogOut size={14} /> <span className="hidden sm:inline">Déconnexion</span></button>
        </div>
      </aside>
      <main className="p-4 sm:p-8 lg:p-10 min-w-0"><Outlet /></main>
    </div>
  );
}

export const StatusBadge = ({ value, labels = COMMERCIAL }) => (
  <span className="k-badge" style={TONE[value] ? { color: TONE[value], borderColor: TONE[value] + "66" } : undefined}>{labels[value] || value}</span>
);
